import { useState, useEffect, useMemo } from 'react'
import { prefetchTraxxallData, parseTraxxallDate } from '../components/TraxxallDueList'
import { buildAircraftProjections } from '../lib/utilizationProjection'
import { isOverviewItem, getInspectionLabel, getInspectionFullName } from '../lib/overviewTasks'

// Returns { [reg]: [{ label, date }] } for all overview inspection items across fleet aircraft.
// Fetches Traxxall due list data per registration (cached after first load) and applies
// the same effective-date logic as the fleet calendar: SchedAero projection → estimated → hard due.
export function useInspectionDueDates(registrations) {
  const [allCalData, setAllCalData] = useState({})
  const regKey = [...registrations].sort().join(',')

  useEffect(() => {
    if (!registrations.length) return
    for (const reg of registrations) {
      prefetchTraxxallData(reg)
        .then(result => {
          if (!result?.items) return
          setAllCalData(prev => ({
            ...prev,
            [reg]: { items: result.items, actuals: result.actuals || [] },
          }))
        })
        .catch(() => {})
    }
  }, [regKey]) // eslint-disable-line react-hooks/exhaustive-deps

  return useMemo(() => {
    if (!Object.keys(allCalData).length) return {}
    const projections = buildAircraftProjections(allCalData)
    const out = {}

    for (const [reg, d] of Object.entries(allCalData)) {
      if (!d?.items?.length) continue
      const regProjs = projections[reg]
      const entries  = []

      for (const item of d.items) {
        if (!isOverviewItem(item)) continue
        const label = getInspectionLabel(item)
        if (!label) continue

        const proj     = regProjs?.get(item)
        const projDate = proj?.projDate || null
        const estDate  = parseTraxxallDate(item['estimated Due Date'])
        const calDate  = parseTraxxallDate(item['next Due Date'])

        // Priority: our projection wins over Traxxall estimated dates.
        // A firm calendar hard due date can override the projection only if it's sooner.
        // Traxxall's estimated due date is only used as a fallback when no projection exists.
        let date
        if (projDate) {
          date = calDate && calDate < projDate ? calDate : projDate
        } else {
          const candidates = [calDate, estDate].filter(Boolean)
          if (!candidates.length) continue
          date = new Date(Math.min(...candidates))
        }

        entries.push({ label, date, fullName: item['description'] || getInspectionFullName(item) || label })
      }

      if (entries.length) out[reg] = entries
    }

    return out
  }, [allCalData])
}
